// TES-1117 S3 — the onboarding funnel per journey: the filter, the period,
// and the rows of a journey's own funnel. Pure, for `node --test`.
import {
  JOURNEY_FUNNEL_PERIOD_LABEL,
  JOURNEY_FUNNEL_PERIODS,
  JOURNEY_FUNNEL_STEP_LABEL,
  OPTIONAL_JOURNEY_FUNNEL_STEPS,
  type JourneyFunnel,
  type JourneyFunnelPeriod,
  type JourneyFunnelStepKey,
  type PatientGroup,
} from "@care-by-ella/contracts";
import { percentOf } from "./onboarding-funnel";

/** Emelie's filter, verbatim: "Alla / Ny behandling / Receptövertag". */
export const JOURNEY_FILTERS: { key: PatientGroup | null; label: string }[] = [
  { key: null, label: "Alla" },
  { key: "NEW_TREATMENT", label: "Ny behandling" },
  { key: "PRESCRIPTION_TRANSFER", label: "Receptövertag" },
];

/** Ö-354, verbatim: "7 dagar / 30 dagar / 90 dagar / Alla". */
export const JOURNEY_PERIOD_FILTERS: {
  key: JourneyFunnelPeriod;
  label: string;
}[] = JOURNEY_FUNNEL_PERIODS.map((key) => ({
  key,
  label: JOURNEY_FUNNEL_PERIOD_LABEL[key],
}));

/**
 * Ö-353, verbatim, with Ö-355's rename ("Avhopp" becomes "Inaktiv 7+ dagar"
 * everywhere): the note under "Eventuell komplettering".
 */
export const CONDITIONAL_STEP_NOTE =
  "Villkorsstyrt steg – räknas inte som inaktiv 7+ dagar";

/** Ö-355, verbatim: what "Pågående" and "Inaktiv 7+ dagar" mean. */
export const JOURNEY_PROGRESS_EXPLANATION =
  "Pågående betyder att patienten har påbörjat den del av resan som hör till hennes patienttyp och ännu inte avslutat den. Inaktiv 7+ dagar betyder att patienten inte har gått vidare eller varit aktiv under minst sju dagar. Patienten kan återuppta resan senare.";

/** Ö-353, verbatim: a step nobody stands at. */
export function emptyJourneyStepText(step: JourneyFunnelStepKey): string {
  return `Ingen patient befinner sig i steget ”${JOURNEY_FUNNEL_STEP_LABEL[step]}” just nu.`;
}

export type JourneyFunnelRow = {
  key: JourneyFunnelStepKey;
  label: string;
  count: number;
  /** Of the journey's people in the period — never of another journey. */
  percent: number | null;
  /** Ö-354: of those standing at the step, still in an ongoing journey. */
  ongoing: number;
  /** Ö-354: of those standing at the step, dropped out. */
  droppedOut: number;
  /** "Eventuell komplettering": counted, but only some have to pass it. */
  conditional: boolean;
};

export function journeyFunnelRows(funnel: JourneyFunnel): JourneyFunnelRow[] {
  return funnel.steps.map((s) => ({
    key: s.key,
    label: JOURNEY_FUNNEL_STEP_LABEL[s.key],
    count: s.count,
    percent: percentOf(s.count, funnel.size),
    ongoing: s.ongoing,
    droppedOut: s.droppedOut,
    conditional: OPTIONAL_JOURNEY_FUNNEL_STEPS.has(s.key),
  }));
}
